// Granola branch — no OAuth. The user pastes their personal Granola API key, which we POST
// to the granola-connect surface route; that route validates the key against the real
// Granola API before persisting it (encrypted) and kicks off the first sync. A 400 means
// the key was rejected; anything else is transient. On success we advance immediately (the
// connect is synchronous — no popup, no readiness poll needed).

import { useState } from 'react';
import { apiFetch, ApiError } from '../../lib/api';
import { SourceIcon } from '../shared/SourceIcon';

export function GranolaConnectStep({ onConnected, onBack }: { onConnected: () => void; onBack: () => void }) {
  const [apiKey, setApiKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const connect = async () => {
    const key = apiKey.trim();
    if (!key) return;
    setBusy(true);
    setError(null);
    try {
      await apiFetch('/api/surfaces/sources/granola-connect', {
        method: 'POST',
        body: JSON.stringify({ apiKey: key }),
      });
      onConnected();
    } catch (e) {
      const rejected = e instanceof ApiError && e.status === 400;
      setError(
        rejected
          ? 'That key was rejected — copy it again from Granola settings and retry.'
          : "Couldn't reach Granola just now — try again in a moment."
      );
      setBusy(false);
    }
  };

  return (
    <>
      <div className="crumb">STEP 02 · GRANOLA</div>
      <div className="wh">Connect <span className="em">Granola</span>.</div>
      <p className="wp">
        Paste your Granola API key (Granola → Settings → API). Terum reads your meeting notes
        read-only and distills them into your knowledge — it never edits anything.
      </p>
      <div style={{ marginTop: 22, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span className="ltile" style={{ width: 30, height: 30 }}><SourceIcon id="granola" size={20} /></span>
        <input
          type="password"
          placeholder="Granola API key"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void connect(); }}
          disabled={busy}
          style={{
            flex: 1, minWidth: 220, padding: '8px 12px', borderRadius: 8,
            border: '1px solid var(--hair2)', background: 'var(--card)',
            color: 'var(--ink)', fontSize: 13,
          }}
        />
        <button className="btn btn-primary" onClick={connect} disabled={!apiKey.trim() || busy}>
          {busy ? 'Connecting…' : 'Connect'}
        </button>
      </div>
      {error && (
        <div style={{ marginTop: 10, fontSize: 12, color: 'var(--rose)' }}>⚠ {error}</div>
      )}
      <div className="wiz-foot">
        <button className="btn btn-secondary" onClick={onBack}>← Back to sources</button>
        <span className="prog">Step 2 · Granola</span>
      </div>
    </>
  );
}
